// LoginPage.js — super_admin support + AuthContext integration

import React, { useState } from 'react';
import './LoginPage.css';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useAuth } from "../auth/AuthContext";
import { API_BASE_URL } from "../constants";

// selected role (radio) + API role 
// (null = mismatch)
function resolveDestination(selectedRole, apiRole) {
    if (selectedRole === 'admin' && apiRole === 'super_admin') {
        return { path: '/admin/care-coordinators', welcome: 'Welcome, Super Admin!' };
    }
    if (selectedRole === 'admin' && apiRole === 'admin') {
        return { path: '/user-analytics', welcome: 'Welcome back, Admin!' };
    }
    if (selectedRole === 'caretaker' && apiRole === 'care_coordinator') {
        return { path: '/care-coordinator', welcome: 'Welcome back, Caretaker!' };
    }
    return null;
}

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [userRole, setUserRole] = useState('caretaker'); // 'caretaker' | 'admin'
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    const handleLogin = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/care-coordinator/auth`,
                { action: "login", email, password },
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            const { access_token, user } = response.data.data;
            const apiRole = user.user_type; // 'admin' | 'care_coordinator' | 'super_admin'

            const destination = resolveDestination(userRole, apiRole);

            if (!destination) {
                Swal.fire({
                    title: 'Login Failed',
                    text: 'You do not have permission to log in with this role.',
                    icon: 'error',
                    confirmButtonColor: '#d33',
                    confirmButtonText: 'Try Again',
                });
                return;
            }

            // Session persist + state update
            login(access_token, {
                id: user.id,
                role: apiRole,
                name: user.name || user.full_name || user.username || email,
            });

            await Swal.fire({
                title: 'Login successful',
                text: destination.welcome,
                icon: 'success',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Continue',
            });
            navigate(destination.path);
        } catch (error) {
            console.error('❌ Login Error:', error.response?.data || error.message);
            Swal.fire({
                title: 'Login Failed',
                text: error.response?.data?.message || 'Invalid credentials',
                icon: 'error',
                confirmButtonColor: '#d33',
                confirmButtonText: 'Try Again',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-box">
                <h2>{userRole === 'caretaker' ? 'Caretaker Login' : 'Admin Login'}</h2>
                <p>Access your dashboard to manage patient care and appointments.</p>
                <div className="role-selection">
                    <label>
                        <input
                            type="radio"
                            value="caretaker"
                            checked={userRole === 'caretaker'}
                            onChange={() => setUserRole('caretaker')}
                        />
                        Login as Caretaker
                    </label>
                    <label>
                        <input
                            type="radio"
                            value="admin"
                            checked={userRole === 'admin'}
                            onChange={() => setUserRole('admin')}
                        />
                        Login as Admin
                    </label>
                </div>
                <form className="login-form" onSubmit={handleLogin}>
                    <input
                        type="text"
                        placeholder="Username or Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    <button type="submit" disabled={loading}>
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                </form>
                <div className="login-footer">
                    <Link to="/forgot-password" className='italic-link'>Forgot password</Link>
                    <p>Don't have an account? <Link to="/caretaker-registration" className='italic-link'>Register</Link></p>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;